import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import { GeneralButton, ConfirmationPopup } from "../../../components";
import { AccountType } from "../../../interface/GeneralResponse";
import {
  resetUserDetails,
  setUserDetails,
} from "../../../redux/reducers/registrationSlice";
import { TFunction } from "i18next";
import { useSelector } from "react-redux";
import { Constants } from "../../../Constants/ApiConstants";
import { EndPoints } from "../../../Constants/endpoints";
import AuthLayout from "../../../Layout/AuthLayout";
import { RootState } from "../../../redux/store";
import { useAppDispatch, useAppSelector, useGetData } from "../../../hooks";
import { AppConfig } from "../../../api/config/config";
import { capitalizeEachWord } from "../../../utils/utils";

interface AccountTypeFormInputs {
  AccountTypeId: number;
}

const SelectAccountType: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const userDetails = useAppSelector((state) => state.registration.userDetails);
  const [isModalOpen, setIsModalOpen] = useState(false); // For go back confirmation

  const { data, error, isLoading } = useGetData<AccountType[]>(
    EndPoints.SelectAccountType,
  );

  const { isPassedOptional, isAuthenticated } = useSelector(
    (state: RootState) => state.user,
  );
  const UserInfo = useSelector((state: RootState) => state.registration);

  // Function to create the validation schema with dynamic error messages
  const createSelectAccountTypeSchema = (t: TFunction) =>
    z.object({
      AccountTypeId: z.number().min(1, t("choose_your_account_type")), // Dynamic error message
    });

  // Memoize the validation schema to recreate it when `t` changes
  const selectAccountTypeSchema = useMemo(
    () => createSelectAccountTypeSchema(t),
    [t],
  );

  // Initialize the form methods using react-hook-form and zodResolver
  const methods = useForm<AccountTypeFormInputs>({
    resolver: zodResolver(selectAccountTypeSchema),
    defaultValues: {
      AccountTypeId: userDetails.AccountTypeId
        ? parseInt(userDetails.AccountTypeId, 10)
        : 0,
    },
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitted },
    setValue,
    trigger,
  } = methods;

  // Re-trigger validation when language changes, but only if the form has been submitted
  useEffect(() => {
    if (isSubmitted) {
      trigger();
    }
  }, [t, trigger, isSubmitted]);

  // Handle navigation based on authentication state
  useEffect(() => {
    if (isPassedOptional) navigate("/", { replace: true });
    else if (!isAuthenticated) navigate("/login", { replace: true });
  }, [isPassedOptional, isAuthenticated, navigate]);

  // Handle browser navigation (back button)
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      setIsModalOpen(true);
    };
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [location.pathname]);

  // Handle page unload (refresh, close tab)
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = ""; // Required for Chrome
      setIsModalOpen(true);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const onSubmit: SubmitHandler<AccountTypeFormInputs> = (formData) => {
    if (formData.AccountTypeId !== 0) {
      dispatch(
        setUserDetails({
          AccountTypeId: formData.AccountTypeId.toString(),
        }),
      );
      navigate("/upload-degree-document");
    }
  };

  const handleAccountTypeSelection = (typeId: number) => {
    setValue("AccountTypeId", typeId);
    dispatch(
      setUserDetails({
        AccountTypeId: typeId.toString(),
      }),
    );
  };

  // Set the form value based on Redux store when AccountTypeId changes
  useEffect(() => {
    if (userDetails.AccountTypeId) {
      const typeId = parseInt(userDetails.AccountTypeId, 10);
      if (!isNaN(typeId)) {
        setValue("AccountTypeId", typeId);
      }
    }
  }, [userDetails.AccountTypeId, setValue]);

  const handleConfirmGoBack = () => {
    dispatch(resetUserDetails());
    setIsModalOpen(false);
    navigate("/login");
  };

  if (isLoading) {
    return <div>{t("loading")}...</div>;
  }

  if (error) {
    return (
      <div className="text-error">
        {t("failed_to_load_account_types")}: {error.message}
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <AuthLayout
        title={
          t("welcome_dr") +
          " " +
          capitalizeEachWord(UserInfo?.userDetails?.FullName)
        }
        description={t("choose_your_type")}
      >
        <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-28 mt-6">
            {data?.Data.map((type: AccountType) => {
              const isSelected =
                parseInt(userDetails.AccountTypeId, 10) === type.Id;
              return (
                <button
                  key={type.Id}
                  type="button"
                  className={`main-button mb-2 ${
                    isSelected
                      ? "bg-primary-bg text-primary-text !border-primary font-bold"
                      : ""
                  } `}
                  onClick={() => handleAccountTypeSelection(type.Id)}
                  aria-pressed={isSelected}
                >
                  {type.Icon ? (
                    <img
                      src={AppConfig.SERVER_URL + type.Icon.replace(/\\/g, "/")}
                      alt={t(type.NameEn.toLowerCase())}
                      className="h-6 w-6"
                    />
                  ) : (
                    <div className="bg-primary-bg h-6 w-6 rounded-full" />
                  )}
                  <span className={`${isSelected ? "font-bold" : " "}`}>
                    {t(type.NameEn.toLowerCase())}
                  </span>
                </button>
              );
            })}
            {errors.AccountTypeId && (
              <p className="text-sm text-error">
                {errors.AccountTypeId.message}
              </p>
            )}
          </div>
          <GeneralButton
            type="submit"
            title={t("continue")}
            color="blue"
            block
            disabled={parseInt(userDetails.AccountTypeId, 10) === 0}
            ariaLabel={t("continue")}
          />
        </form>
      </AuthLayout>
      {isModalOpen && (
        <ConfirmationPopup
          isModalOpen={isModalOpen}
          title={t("all_info_will_be_lost")}
          description={t("are_you_sure_to_do_action")}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleConfirmGoBack}
          ariaLabel={t("confirm_navigation")}
        />
      )}
    </FormProvider>
  );
};

export default SelectAccountType;
